import { logger } from '@garnish/logger';

import { PLACES_GEOCODE_URL } from '../config';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches GEO data for a given place ID (or address) using Google Places API.
 */
export async function fetchGeoData(params: Params) {
  const { placeId, placeAddress, apiKey } = params;

  const placeIdResponse = await fetchGeoDataByPlaceId(placeId, apiKey);
  const shouldUseFallback = placeIdResponse.resultsNumber === 0 && placeAddress !== undefined; // prettier-ignore

  log.info('Used "search by place ID" method.', {
    resultsNumber: placeIdResponse.resultsNumber,
  });

  if (shouldUseFallback) {
    log.warn('Failed to fetch data by place ID, using address search instead.', {
      invalidPlaceId: placeId,
      details: placeIdResponse.data,
    });

    const addressResponse = await fetchGeoDataByAddress(placeAddress, apiKey);

    log.info('Used "search by address" method.', {
      resultsNumber: addressResponse.resultsNumber,
    });

    return addressResponse.data ?? FALLBACK_DATA;
  }

  return placeIdResponse.data ?? FALLBACK_DATA;
}

// ─── Search Methods ──────────────────────────────────────────────────────────

async function fetchGeoDataByPlaceId(placeId: string, apiKey: string) {
  const url = `${PLACES_GEOCODE_URL}?place_id=${placeId}&key=${apiKey}`;
  const response = await fetch(url);

  const data: google.maps.GeocoderResponse | undefined = await response.json();
  const resultsNumber = data ? data?.results?.length : 0;

  return { data, resultsNumber };
}

async function fetchGeoDataByAddress(placeAddress: string, apiKey: string) {
  const url = `${PLACES_GEOCODE_URL}?address=${placeAddress}&key=${apiKey}`;
  const response = await fetch(url);

  const data: google.maps.GeocoderResponse | undefined = await response.json();
  const resultsNumber = data ? data?.results?.length : 0;

  return { data, resultsNumber };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FALLBACK_DATA: google.maps.GeocoderResponse = {
  results: [],
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  apiKey: string;
  placeId: string;

  /**
   * A raw place address that can be used fo searching in case place ID search fails.
   */
  placeAddress?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// logger.enable('FETCH_GEO_DATA');

const log = logger.extend('FETCH_GEO_DATA');
